import { useEffect } from 'react';
import { useLocation } from 'wouter';
import { pushEvent } from '@/lib/analytics';

const SCROLL_MILESTONES = [25, 50, 75, 100] as const;
const TIME_MILESTONES = [15, 30, 60, 180] as const;

export function useEngagementTracking(): void {
  const [location] = useLocation();

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const firedScroll = new Set<number>();
    const firedTime = new Set<number>();

    const computeScrollPercent = (): number | null => {
      const doc = document.documentElement;
      const body = document.body;
      const scrollTop = window.scrollY || doc.scrollTop || body.scrollTop || 0;
      const viewport = window.innerHeight || doc.clientHeight || 0;
      const fullHeight = Math.max(
        doc.scrollHeight,
        body.scrollHeight,
        doc.offsetHeight,
        body.offsetHeight,
        doc.clientHeight,
      );
      const scrollable = Math.max(fullHeight - viewport, 0);
      if (scrollable <= 0) return null;
      return Math.min(100, Math.round((scrollTop / scrollable) * 100));
    };

    const handleScroll = () => {
      const percent = computeScrollPercent();
      if (percent === null) return;
      for (const milestone of SCROLL_MILESTONES) {
        if (percent >= milestone && !firedScroll.has(milestone)) {
          firedScroll.add(milestone);
          pushEvent('scroll_depth', {
            percent: milestone,
            page_path: window.location.pathname + window.location.search,
          });
        }
      }
    };

    const timers = TIME_MILESTONES.map((seconds) =>
      window.setTimeout(() => {
        if (firedTime.has(seconds)) return;
        firedTime.add(seconds);
        pushEvent('time_on_page', {
          seconds,
          page_path: window.location.pathname + window.location.search,
        });
      }, seconds * 1000),
    );

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      timers.forEach((id) => window.clearTimeout(id));
    };
  }, [location]);
}
